import * as React from 'react';
import { AppContext } from '../../../App';
import { Checkbox, TableCell, TableRow, Tooltip, Chip } from '@mui/material';
import { Link, useParams, useLocation, useNavigate } from 'react-router';
import { DocumentDispatchContext } from '../../../context/DocumentContext';
import { useAtom } from 'jotai';
import { documentDataEditDialogIdAtom } from '../../../atoms/review-atoms';

import DownloadExtraction from './DownloadExtraction';
import DownloadDocument from '../../common/DownloadDocument';
import DeleteExtraction from './DeleteExtraction';
import DocumentDataStatus from './DocumentDataStatus';

import { format } from 'date-fns';
import { getIdFromUrl } from '../../../utils/getIdFromUrl';

import { Box } from '@mui/material';

import {
  IDocument,
  IExtraction,
  ExtractionStatus,
  DataProcessingStatus,
} from '../../../types/my-types';

import PreviewIcon from '@mui/icons-material/Preview';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LinkTableStyled } from '../../common/StyledComponents';
import { useTheme } from '@mui/material/styles';

export interface ExtractionBatchExtractionListRowProps {
  extraction: IExtraction;
  document: IDocument | undefined;
}

const ExtractionBatchExtractionListRow: React.FC<ExtractionBatchExtractionListRowProps> = ({
  extraction,
  document,
}) => {
  const { dpid, id: eid } = useParams<{ dpid: string; id: string }>();
  const { isStaff, setToast } = React.useContext(AppContext);
  const dispatch = React.useContext<React.Dispatch<any>>(DocumentDispatchContext);
  const [, setDocumentDataEditDialogId] = useAtom(documentDataEditDialogIdAtom);

  const navigate = useNavigate();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const selectedIds = searchParams.get('selectedIds')?.split(',') || [];

  const selected = selectedIds.includes(extraction.id);

  const handleChange = () => {
    if (selected) {
      const tempIds = selectedIds.filter((id) => id !== extraction.id);

      if (tempIds.length === 0) {
        searchParams.delete('selectedIds');
        navigate({
          search: searchParams.toString(),
        });
      } else {
        searchParams.set('selectedIds', tempIds.join(','));
        navigate({
          search: searchParams.toString(),
        });
      }
    } else {
      const tempIds = selectedIds;

      if (!tempIds.includes(extraction.id)) {
        tempIds.push(extraction.id);
      }

      searchParams.set('selectedIds', tempIds.join(','));
      navigate({
        search: searchParams.toString(),
      });
    }
  };

  const theme = useTheme();

  if (!document) return null;

  const statusDisplay = (status: DataProcessingStatus | ExtractionStatus) => {
    if (!status) return 'Unknown';
    return status === 'Failed' ? (
      <Chip
        label={status}
        size="small"
        sx={{
          backgroundColor: '#f44336',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '0.75rem',
        }}
      />
    ) : (
      status
    );
  };

  return (
    <TableRow>
      <TableCell
        align="center"
        sx={{
          width: '40px',
          px: '4px !important',
          borderLeft: selected ? '4px solid #65BBA9' : '4px solid transparent',
        }}
        onClick={handleChange}
      >
        <Checkbox
          inputProps={
            {
              'aria-label': 'secondary checkbox',
              'data-cy': `checkbox-extraction-batch-extraction-table-row`,
            } as React.InputHTMLAttributes<HTMLInputElement>
          }
          checked={selected}
          onChange={handleChange}
          size="small"
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <LinkTableStyled
            to={`/data-pools/${dpid}/documents/${document.id}`}
            data-cy={`link-doc-details-${document.id}`}
          >
            {document.id}
          </LinkTableStyled>

          <span
            onClick={() => {
              navigator.clipboard.writeText(document?.id || '');
              setToast({
                open: true,
                message: 'Copied!',
                variant: 'success',
              });
            }}
          >
            <ContentCopyIcon style={{ fontSize: '14px', cursor: 'pointer' }} />
          </span>

          <Tooltip title={'Show Document In Manifest'} placement="top">
            <Link
              to={`/data-pools/${dpid}/documents?id=${document?.id}`}
              style={{ textDecoration: 'none' }}
            >
              <FilterAltIcon style={{ color: 'black', fontSize: '12px', marginLeft: '5px' }} />
            </Link>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell align="center">
        {statusDisplay(extraction.status)}
      </TableCell>
      <TableCell align="center">
        {statusDisplay(extraction.data_processing_status)}
      </TableCell>
      <TableCell align="center">
        <DocumentDataStatus
          status={extraction.document_data_status}
          exclusionReason={extraction.exclusion_reason}
          escalationDetail={extraction.escalation_detail}
          onClick={() => {
            if (extraction.document_data) {
              const docDataId = getIdFromUrl(extraction.document_data);
              setDocumentDataEditDialogId({
                id: docDataId,
                documentId: extraction.document,
                currentStatus: extraction.document_data_status,
                currentExclusionReason: extraction.exclusion_reason,
                currentEscalationDetail: extraction.escalation_detail,
              });
            } else {
              setToast({
                variant: 'error',
                open: true,
                message: 'Document data not available',
              });
            }
          }}
        />
      </TableCell>
      <TableCell align="center">
        {document?.label}
      </TableCell>
      <TableCell align="center">
        {document?.created_at
          ? format(new Date(document?.created_at), 'yyyy-MM-dd HH:mm:ss')
          : null}
      </TableCell>
      <TableCell align="center">
        {extraction && extraction.finished
          ? format(new Date(extraction.updated_at), 'yyyy-MM-dd HH:mm:ss')
          : 'In Progress'}
      </TableCell>

      <TableCell align="center" width="165">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <DownloadExtraction
            failed={extraction.status === 'Failed'}
            extractionId={extraction.id}
            docLabel={document?.label}
          />
          <Tooltip
            placement="top"
            title={`View Document`}
            disableFocusListener
            disableTouchListener
            PopperProps={{
              popperOptions: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 0],
                    },
                  },
                ],
              },
            }}
          >
            <div>
              <VisibilityIcon
                data-cy="icon-view-document"
                style={{
                  color: theme.palette.custom.lightGreen,
                  cursor: 'pointer',
                  paddingTop: '5px',
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  dispatch({
                    type: 'TOGGLE_VIEW_DOCUMENT_MODAL',
                    pages: document?.pages,
                    id: document?.id,
                  });
                }}
              />
            </div>
          </Tooltip>
          <Link
            to={`/data-pools/${dpid}/extraction-batch/${eid}/review/${extraction.id}`}
            style={{ textDecoration: 'none' }}
          >
            <Tooltip
              placement="top"
              title={`Review`}
              disableFocusListener
              disableTouchListener
              PopperProps={{
                popperOptions: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 0],
                      },
                    },
                  ],
                },
              }}
            >
              <PreviewIcon style={{ color: theme.palette.custom.lightGreen, paddingTop: '5px' }} />
            </Tooltip>
          </Link>
          {isStaff && document ? (
            <DownloadDocument
              documentId={document.id}
              documentContentType={document.content_type}
              documentLabel={document.label}
              documentFileExtension={document.file_extension}
              iconType="file"
            />
          ) : null}
          <DeleteExtraction extractionId={extraction.id} extractionBatchLabel={document?.label} />
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default ExtractionBatchExtractionListRow;
